import { useState, useEffect, useMemo, useReducer } from "react";
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import dayjs from 'dayjs';
import { Button, Modal } from "react-bootstrap";
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import { InlineDateInput } from '../../Components/Reusable/Pickers';
import CustomButton from "../../Components/Reusable/Button/CustomButton.js";
import BackButton from "../../Components/Reusable/BackButton/BackButton.js";
import { usePagination } from "../../Components/Reusable/TablePagination/Functions";
import TablePaginationFooter from "../../Components/Reusable/TablePagination/TableFooter";
import './Roster.css';
import {
  GetBarbers,
  CreateLeave,
  GetRoster,
  UpdateLeave,
  DeleteLeave
} from './Requests.js';
import { Link } from "react-router-dom";
import Select from "react-select";
import { ToastContainer } from "react-toastify";
import { notifySuccess, notifyError } from '../../Response/ResposeHandler.js';
import { useTranslation } from "react-i18next";

const initialState = false;

const reducer = (state, action) => {
  switch (action.type) {
    case 'addLeaveOpen':
      return { ...state, addLeave: true };
    case 'addLeaveClose':
      return { ...state, addLeave: false };
    case 'editLeaveOpen':
      return { ...state, editLeave: true };
    case 'editLeaveClose':
      return { ...state, editLeave: false };
    default:
      return state;
  }
};

const Roster = (props) => {
  const { t } = useTranslation("roster");
  const [state, dispatch] = useReducer(reducer, initialState);
  const [staff, setStaff] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState();
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();
  let errorsObj = { staff: '', startDate: '', endDate: '' };
  const [errors, setErrors] = useState(errorsObj);
  const [isLoading, setIsloading] = useState(true);
  const [roster, setRoster] = useState();
  const [rosterData, setRosterData] = useState();
  const [showLeaveOnly, setShowLeaveOnly] = useState(false);
  const [leaveToEdit, setLeaveToEdit] = useState();
  const [selectDate, setSelectedDate] = useState(new Date());
  const [addLeaveButtonLoading, setAddLeaveButtonLoading] = useState(false);
  const [deleteLeaveButtonLoading, setDeleteLeaveButtonLoading] = useState(false);

  useEffect(() => {
    GetStaffWrapper();
    OnDateChange(new Date());
  }, []);

  const total = roster?.length ?? 0;
  const { page, setPage, pageSize, setPageSize, start, end, canPrev, canNext } =
    usePagination(total);
  const paged = useMemo(() => (roster ? roster.slice(start, end) : []), [roster, start, end]);

  function ResetFields() {
    setSelectedStaff();
    setStartDate();
    setEndDate();
    setErrors({ staff: '', startDate: '', endDate: '' });
    setLeaveToEdit();
  }

  function GetStaffWrapper() {
    GetBarbers().then((res) => {
      var tempVar = [];
      res.data.map((item) => {
        var obj = { value: item.barberId, label: item.name, isDisabled: item.status === 1 ? false : true };
        tempVar.push(obj);
        return null;
      });
      setStaff(tempVar);
    }).catch(() => {
    }).finally(() => {
    });
  }

  function SubmitLeave(e) {
    e.preventDefault();
    let error = false;
    const errorObj = { ...errorsObj };

    if (selectedStaff === undefined || selectedStaff === null) {
      errorObj.staff = t('roster.errors.staffRequired');
      error = true;
    }

    if (startDate === undefined || startDate === null) {
      errorObj.startDate = t('roster.errors.startDateRequired');
      error = true;
    }

    if (endDate === undefined || endDate === null) {
      errorObj.endDate = t('roster.errors.endDateRequired');
      error = true;
    } else {
      if (endDate < startDate) {
        errorObj.endDate = t('roster.errors.endBeforeStart');
        error = true;
      }
    }

    setErrors(errorObj);

    if (error) {
      return;
    } else {
      setAddLeaveButtonLoading(true);
      CreateLeave(
        selectedStaff.value,
        startDate,
        endDate
      ).then((res) => {
        if (res.status === 200) {
          notifySuccess(t('roster.toasts.leaveSaved'));
        }
      }).catch((error) => {
        if (error?.response?.data === "You have a leave booked that is within these dates") {
          notifyError(t('roster.toasts.leaveOverlapError'));
        } else {
          notifyError(t('roster.toasts.leaveSaveError'));
        }
      }).finally(() => {
        OnDateChange(selectDate);
        dispatch({ type: 'addLeaveClose' });
        setAddLeaveButtonLoading(false);
      });
    }
  }

  function SubmitEditLeave(e) {
    e.preventDefault();
    let error = false;
    const errorObj = { ...errorsObj };

    if (selectedStaff === undefined || selectedStaff === null) {
      errorObj.staff = t('roster.errors.staffRequired');
      error = true;
    }

    if (startDate === undefined || startDate === null) {
      errorObj.startDate = t('roster.errors.startDateRequired');
      error = true;
    }

    if (endDate === undefined || endDate === null) {
      errorObj.endDate = t('roster.errors.endDateRequired');
      error = true;
    } else {
      if (endDate < startDate) {
        errorObj.endDate = t('roster.errors.endBeforeStart');
        error = true;
      }
    }

    setErrors(errorObj);

    if (error) {
      return;
    } else {
      setAddLeaveButtonLoading(true);
      UpdateLeave(
        leaveToEdit,
        selectedStaff.value,
        startDate,
        endDate
      ).then((res) => {
        if (res.status === 200) {
          notifySuccess(t('roster.toasts.leaveUpdated'));
        }
        dispatch({ type: 'addLeaveClose' });
      }).catch((error) => {
        if (error?.response?.data === "You have a leave booked that is within these dates") {
          notifyError(t('roster.toasts.leaveOverlapError'));
        } else {
          notifyError(t('roster.toasts.leaveUpdateError'));
        }
      }).finally(() => {
        OnDateChange(selectDate);
        dispatch({ type: 'editLeaveClose' });
        setAddLeaveButtonLoading(false);
      });
    }
  }

  function SubmitDeleteLeave(e) {
    e.preventDefault();
    setDeleteLeaveButtonLoading(true);
    DeleteLeave(
      leaveToEdit,
    ).then((res) => {
      if (res.status === 200) {
        notifySuccess(t('roster.toasts.leaveDeleted'));
      }
      dispatch({ type: 'addLeaveClose' });
    }).catch(() => {
      notifyError(t('roster.toasts.leaveDeleteError'));
    }).finally(() => {
      OnDateChange(selectDate);
      dispatch({ type: 'editLeaveClose' });
      setDeleteLeaveButtonLoading(false);
    });
  }

  function OnDateChange(date) {
    setSelectedDate(date);
    setIsloading(true);
    GetRoster(date).then((res) => {
      if (res.status === 200) {
        setRosterData(res.data);
        if (showLeaveOnly) {
          setRoster(res.data.filter(x => x.leaveId !== null));
        } else {
          setRoster(res.data);
        }
      }
    }).catch(() => {
    }).finally(() => {
      setIsloading(false);
    });
  }

  function onEditLeaveClick(val) {
    setSelectedStaff(staff.filter(x => x.value === val.staffId)[0]);
    setStartDate(dayjs(val.startDateTime));
    setEndDate(dayjs(val.endDateTime));
    setLeaveToEdit(val.leaveId);

    dispatch({ type: 'editLeaveOpen' });
  }

  function onToggleChange(e) {
    if (e.target.checked) {
      setRoster(roster.filter(x => x.leaveId !== null));
    } else {
      setRoster(rosterData);
    }
    setShowLeaveOnly(e.target.checked);
  }

  const ClearIndicatorStyles = (base, state) => ({
    ...base,
    cursor: "pointer",
    color: state.isFocused ? "#525252" : "#7e7e7e",
  });

  const ClearIndicator = (props) => {
    const {
      getStyles,
      innerProps: { ref, ...restInnerProps },
    } = props;
    return (
      <div
        {...restInnerProps}
        ref={ref}
        style={getStyles("clearIndicator", props)}
      >
        <div style={{ padding: "0px 5px" }}>{t('roster.actions.clear')}</div>
      </div>
    );
  };

  return (
    <div>
      <ToastContainer />
      <Container style={{ marginTop: "0" }}>
        {!isLoading ?
          <div>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div className="sm-field sm-grid__full m-0">
                <InlineDateInput
                  label=""
                  defaultValue={dayjs()}
                  onChange={(date) => { OnDateChange(date); }}
                  value={selectDate}
                />
              </div>

              <div className="m-0">
                <Link
                  to="#"
                  className="btn-custom btn-primary ms-1"
                  onClick={() => { dispatch({ type: 'addLeaveOpen' }); ResetFields(); }}
                >
                  {t('roster.actions.addLeave')}
                </Link>
              </div>
            </div>
            <div className="roster-toggle-container mt-4 mb-4">
              <span className='me-2 payConnect__sub'>{t('roster.labels.showLeaveOnly')}</span>
              <input
                onChange={(e) => { onToggleChange(e); }}
                className="toggle-switch"
                type="checkbox"
                checked={showLeaveOnly}
                id="logon"
              />
              <label htmlFor='logon' className="switch-label" style={{ marginBottom: 0, marginLeft: "0.6rem" }} />
            </div>
          </div>
          : null}

        <Row>
          <Col style={{ marginLeft: "auto", marginRight: "auto" }}>
            <div className="roster-table-container">
              <div className="data-table-wrapper">

                {isLoading ? <div className="custom-loader-wrapper">
                  <div className="custom-loader"></div>
                </div>
                  :
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>{t('roster.table.name')}</th>
                        <th>{t('roster.table.hours')}</th>
                        <th>{t('roster.table.breaks')}</th>
                        <th>{t('roster.table.leave')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {roster.length === 0 ? <></>
                        :
                        paged.map((val, index) => (
                          <tr key={index}>
                            <td>{val.staffName}</td>
                            <td>{val.workingHours}</td>
                            <td>{val.breaks}</td>
                            <td>
                              {val.leaveId !== null ? ((new Date(val.startDateTime)).getDate() + "/" + (dayjs(val.startDateTime).month() + 1) + " - " + (new Date(val.endDateTime)).getDate() + "/" + ((dayjs(val.endDateTime)).month() + 1)) : '-'}
                              {val.leaveId !== null ?
                                <IconButton onClick={() => { onEditLeaveClick(val); }} aria-label="edit" size="small" style={{ justifyContent: "right", marginLeft: "0.6rem" }}>
                                  <EditIcon fontSize="small" style={{ justifyContent: "right" }} />
                                </IconButton>
                                :
                                <></>
                              }

                            </td>
                          </tr>
                        ))}
                    </tbody>

                  </table>
                }

              </div>
              {!isLoading ?
                roster.length === 0 ?
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <div style={{ marginTop: "2rem" }}>
                      <p className="sm-dull">{t('roster.emptyState.noStaffMembers')}</p>
                    </div>
                  </div>
                  :
                  <></>
                :
                <></>
              }

              {!isLoading ? roster.length > 8 ?
                <TablePaginationFooter
                  total={total}
                  page={page}
                  pageSize={pageSize}
                  setPage={setPage}
                  setPageSize={setPageSize}
                  canPrev={canPrev}
                  canNext={canNext}
                />
                : null : null}

            </div>
          </Col>
        </Row>
      </Container>

      {/* Add Leave Modal */}
      <Modal className="modal fade" show={state.addLeave} centered>
        <div className="sm-modal">
          <div className="sm-modal__header">
            <h5 className="sm-modal__title">{t('roster.modals.addLeaveTitle')}</h5>
            <Button
              variant=""
              type="button"
              className="btn-close"
              data-dismiss="modal"
              onClick={() => { dispatch({ type: 'addLeaveClose' }); }}
            />
          </div>
          <div className="sm-modal__body">
            <form className="sm-form">
              <div className="sm-grid sm-grid--2">

                <div className={`sm-field sm-grid__full ${errors.staff ? "is-error" : ""}`}>
                  <label className="sm-label">
                    {t('roster.labels.selectStaff')} <span className="sm-req">*</span>
                  </label>
                  <Select
                    isSearchable={false}
                    placeholder={t('roster.placeholders.staff')}
                    closeMenuOnSelect={true}
                    components={{ ClearIndicator }}
                    styles={{ clearIndicator: ClearIndicatorStyles }}
                    options={staff}
                    onChange={(e) => { setSelectedStaff({ value: e.value, label: e.label }); }}
                    className="sm-select"
                    classNamePrefix="rs"
                  />
                  {errors.staff && <div className="sm-error">{errors.staff}</div>}
                </div>

                <div className={`sm-field ${errors.startDate ? "is-error" : ""}`}>
                  <InlineDateInput
                    label={t('roster.labels.startDate')}
                    required
                    onChange={(e) => { setStartDate(e); }}
                    error={errors.startDate}
                    value={startDate}
                  />
                </div>

                <div className={`sm-field ${errors.endDate ? "is-error" : ""}`}>
                  <InlineDateInput
                    label={t('roster.labels.endDate')}
                    required
                    onChange={(e) => { setEndDate(e); }}
                    error={errors.endDate}
                    value={endDate}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'end' }}>
                <CustomButton onClick={SubmitLeave} className="app-btn--sm mt-4" label={t('roster.actions.addLeave')} loading={addLeaveButtonLoading} />
              </div>
            </form>
          </div>
        </div>
      </Modal>

      {/* Edit Leave Modal */}
      <Modal className="modal fade" show={state.editLeave} centered>
        <div className="sm-modal">
          <div className="sm-modal__header">
            <h5 className="sm-modal__title">{t('roster.modals.editLeaveTitle')}</h5>
            <Button
              variant=""
              type="button"
              className="btn-close"
              data-dismiss="modal"
              onClick={() => { dispatch({ type: 'editLeaveClose' }); }}
            />
          </div>
          <div className="sm-modal__body">
            <form className="sm-form">
              <div className="sm-grid sm-grid--2">

                <div className={`sm-field sm-grid__full ${errors.staff ? "is-error" : ""}`}>
                  <label className="sm-label">
                    {t('roster.labels.selectStaff')} <span className="sm-req">*</span>
                  </label>
                  <Select
                    isSearchable={false}
                    placeholder={t('roster.placeholders.staff')}
                    closeMenuOnSelect={true}
                    components={{ ClearIndicator }}
                    styles={{ clearIndicator: ClearIndicatorStyles }}
                    options={staff}
                    defaultValue={selectedStaff}
                    onChange={(e) => { setSelectedStaff({ value: e.value, label: e.label }); }}
                    className="sm-select"
                    classNamePrefix="rs"
                  />
                  {errors.staff && <div className="sm-error">{errors.staff}</div>}
                </div>

                <div className={`sm-field ${errors.startDate ? "is-error" : ""}`}>
                  <InlineDateInput
                    label={t('roster.labels.startDate')}
                    required
                    onChange={(e) => { setStartDate(e); }}
                    error={errors.startDate}
                    value={startDate}
                  />
                </div>

                <div className={`sm-field ${errors.endDate ? "is-error" : ""}`}>
                  <InlineDateInput
                    label={t('roster.labels.endDate')}
                    required
                    onChange={(e) => { setEndDate(e); }}
                    error={errors.endDate}
                    value={endDate}
                  />
                </div>
              </div>
              <div className="sm-actions" style={{ justifyContent: "space-between" }}>
                <BackButton style={{ marginRight: "1rem" }} label={t('roster.actions.delete')} onClick={SubmitDeleteLeave} loading={deleteLeaveButtonLoading} />
                <CustomButton className="app-btn--sm" onClick={SubmitEditLeave} label={t('roster.actions.editLeave')} loading={addLeaveButtonLoading} />
              </div>
            </form>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Roster;
